import { NoopScrollStrategy } from '@angular/cdk/overlay';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, take, takeUntil } from 'rxjs/operators';
import { LoadingOverlayComponent } from '../_shared/components/loading-overlay/loading-overlay.component';
import { PriceSummaryDataService } from '../_shared/components/price-summary/price-summary.data.service';
import { ProductList, ProductListService } from '../_shared/components/product-list/product-list.service';
import { PermissionService } from '../_shared/services/permission.service';
import { Product, ProductGroup, ProductHeadline } from '../_shared/services/products.service';
import { QuoteService } from '../_shared/services/quote.service';
import { SelectedCustomerService } from '../_shared/services/selected-customer.service';
import { SnackBarService } from '../_shared/services/snack-bar.service';
import { SummaryDataService } from './summary.data.service';
import { AppService } from '../_shared/services/app.service';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { isGroupWithHintText } from '../_shared/utils/offer.util';
import { QuoteStatus } from '../_shared/models/quote.model';
import { PrintTypeLong } from '../_shared/models/print-type';
import { Application } from '../_shared/models/application.model';

@Component({
    selector: 'app-summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.scss'],
})
export class SummaryComponent implements OnInit, OnDestroy {
    private ngUnsubscribe: Subject<void> = new Subject<void>();
    private dialogRef: MatDialogRef<LoadingOverlayComponent>;

    private quoteId: string;
    private productList: ProductList;
    public sampleOfferId: string;
    public selectedCustomerId: string;
    public navigationParams: Params;
    public dataSource: MatTableDataSource<Product>;
    public isLoading = true;
    public voucher: string = null;
    public callbackUrl: string;
    public secondaryCallbackUrl: string;
    public externalApp: boolean;
    public orderType: Application;
    public availablePrintTypes: string[];
    public printType: PrintTypeLong;
    public quotationRequestId: string;
    public salesforceIdControl: FormControl<string>;
    public planning: boolean;
    public salesforceIdForPlanning: string;
    public primaryScheme: string;

    constructor(
        private appService: AppService,
        private router: Router,
        private priceSummaryDataService: PriceSummaryDataService,
        private snackBarService: SnackBarService,
        private translateService: TranslateService,
        private activatedRoute: ActivatedRoute,
        private selectedCustomerService: SelectedCustomerService,
        private quoteService: QuoteService,
        private dataService: SummaryDataService,
        private productListService: ProductListService,
        public permissionService: PermissionService,
        private dialog: MatDialog
    ) {}

    public ngOnInit() {
        const { id, documentId, callbackUrl, secondaryCallbackUrl, salesforceId, quotationRequestId, planning } =
            this.activatedRoute.snapshot.queryParams;

        this.appService.salesOrg$
            .pipe(
                takeUntil(this.ngUnsubscribe),
                concatMap((salesOrg) => this.dataService.getAvailablePrintTypes(salesOrg))
            )
            .subscribe((printTypes) => {
                this.availablePrintTypes = printTypes;
                // default should be 'L' if available, otherwise use 'M' or 'S'
                this.printType = <PrintTypeLong>['LONG', 'MIDDLE', 'SHORT'].find((t) => printTypes.includes(t));
            });
        this.quoteId = id || documentId; // to support legacy urls
        this.callbackUrl = callbackUrl;
        this.secondaryCallbackUrl = secondaryCallbackUrl;
        if (salesforceId) {
            if (salesforceId === 'ask' && this.permissionService.isUserAnEmployee) {
                this.salesforceIdControl = new FormControl<string>('', Validators.required);
                this.salesforceIdControl.valueChanges.pipe(distinctUntilChanged()).subscribe((value) => {
                    this.quoteService.updateSalesforceOpportunityId(value);
                });
            } else {
                this.quoteService.updateSalesforceOpportunityId(salesforceId);
            }
        }
        if (quotationRequestId) {
            this.quotationRequestId = quotationRequestId;
        }
        this.planning = Boolean(planning);
        if (this.planning) {
            this.salesforceIdForPlanning = salesforceId;
        }

        this.navigationParams = {
            id: this.quoteId,
            ...(quotationRequestId && { quotationRequestId }),
            ...(this.planning && { planning: true, salesforceId: this.salesforceIdForPlanning }),
        };

        this.quoteService
            .fetchQuote(this.quoteId)
            .pipe(take(1), takeUntil(this.ngUnsubscribe))
            .subscribe((quote) => {
                this.orderType = quote.type;
                this.externalApp = !!quote.externalApp;
                this.primaryScheme = quote.schemes?.primary?.split(';')[0];
                if (quote.status !== QuoteStatus.new) {
                    this.closeDialog();
                    this.router.navigate(['next-steps'], {
                        queryParams: {
                            id: this.quoteId,
                            sapDocumentId: quote.offerNumber,
                            ...(this.planning && { planning: true, salesforceId: this.salesforceIdForPlanning }),
                        },
                    });
                }

                this.isLoading = false;
                if (this.externalApp && this.salesforceIdControl && quote.salesforceOpportunityId) {
                    this.salesforceIdControl.setValue(quote.salesforceOpportunityId);
                }
                const appTitle = this.quoteService.hasConfiguration(quote) ? '' : quote.externalApp;
                this.appService.appTitle$.next(appTitle);
            });
        this.subscribeToProductList$();
        this.subscribeToSelectedCustomer$();
    }

    public get hasProductErrors() {
        return this.productListService.hasErrors;
    }

    public get hasConfiguration() {
        return this.quoteService.hasConfiguration();
    }

    public priceSummaryIsLoading() {
        return this.priceSummaryDataService.isPriceSummaryLoading$;
    }

    public updateVoucherCode(event: string) {
        this.voucher = event;
    }

    public closeOffer() {
        // if currently logged in user is a customer, just use the viCompanyId of the currently logged in user
        const isCustomerLoggedIn =
            this.permissionService.userInfo$.value && this.permissionService.userInfo$.value.role === 'customer';
        let customerIdForClose = 'no-customer-selected';
        if (this.selectedCustomerId) {
            customerIdForClose = this.selectedCustomerId;
        } else if (isCustomerLoggedIn) {
            // use the viCompanyId
            customerIdForClose = this.permissionService.userInfo$.value.viCompanyId;
        }
        const salesforceId = this.quoteService.getCurrentQuote().salesforceOpportunityId;

        this.isLoading = true;

        this.quoteService
            .updatePrintType(this.printType)
            .pipe(concatMap(() => this.closeQuote(customerIdForClose)))
            .subscribe({
                next: ({ salesDocumentNumber: sapDocumentId }) => {
                    this.onSuccess('SUMMARY.SNACK_BAR.CLOSE_OFFER.SUCCESS');
                    this.router.navigate(['next-steps'], {
                        queryParams: {
                            id: this.quoteId,
                            sapDocumentId,
                            callbackUrl: this.secondaryCallbackUrl,
                            ...(this.planning && { planning: true, salesforceId }),
                        },
                    });
                },
                error: () => this.onError('SUMMARY.SNACK_BAR.CLOSE_OFFER.ERROR'),
            });
    }

    public navigateBackToConfiguration() {
        this.router.navigate(['configuration'], { queryParams: this.navigationParams });
    }

    private closeQuote(customerNumber: string) {
        this.addArtificialProducts();
        return this.dataService.closeQuote(
            customerNumber,
            this.quotationRequestId,
            this.voucher || '',
            this.productList,
            this.priceSummaryDataService.getLastPriceSummary()
        );
    }

    private addArtificialProducts() {
        if (
            this.appService.getSalesOrg() === '0500' &&
            !this.quoteService.hasSampleQuote() &&
            ![Application.a2aPlanner, Application.ventilationPlanner].includes(this.orderType)
        ) {
            const waermeStrom: ProductGroup = {
                name: this.translateService.instant('SUMMARY.OFFER_CREATION.ADDITIONAL_INFORMATION.HINT'),
                order: 100,
                translationKey: '',
                products: [],
                printOptions: {
                    printType: 'INDIVIDUAL',
                    textMap: {
                        POS_TEXT: {
                            name: 'POS_TEXT',
                            textItems: [
                                {
                                    textModuleName: null,
                                    textContent: this.translateService.instant(
                                        'SUMMARY.OFFER_CREATION.ADDITIONAL_INFORMATION.TEXT_CONTENT'
                                    ),
                                },
                            ],
                        },
                        POS_AFTER: {
                            name: 'POS_AFTER',
                            textItems: [
                                {
                                    textModuleName: 'ZANG_AC_Muster_1',
                                    textContent: null,
                                },
                            ],
                        },
                    },
                },
            };

            this.productList.groups.push(waermeStrom);
        }
    }

    private subscribeToSelectedCustomer$() {
        this.selectedCustomerService.selectedCustomer$
            .pipe(takeUntil(this.ngUnsubscribe), filter(Boolean))
            .subscribe((customer: { number: string }) => {
                this.selectedCustomerId = customer.number;
            });
    }

    private createDataSourceFromProductList() {
        const data = this.productList.groups
            .filter((group) => !isGroupWithHintText(group))
            .reduce((prev, group) => {
                const groupAsProduct: ProductHeadline = group.products.length
                    ? {
                          groupNameTranslation: group.translationKey,
                          children: group.products.map((p) => p.material),
                      }
                    : undefined;
                const products = group.products.reduce((prev, curr) => [...prev, curr], <Product[]>[]);
                return [...prev, groupAsProduct, ...products].filter(Boolean);
            }, [])
            .map((product: Product) => {
                const formGroup = product.material
                    ? // add edit form controls
                      new UntypedFormGroup({
                          ...(!!this.sampleOfferId && {
                              selected: new FormControl<boolean>({
                                  value: product.selected,
                                  disabled:
                                      !product.alternativeFor && !(product.alternatives.length && !product.selected),
                              }),
                          }),
                          optional: new FormControl<boolean>({
                              value: product.optional && product.selected,
                              disabled: !product.canBeOptional || !product.selected,
                          }),
                          ...((!!this.sampleOfferId || product.category === 'EXTERNAL') && {
                              quantity: new FormControl<number>(product.quantity),
                          }),
                      })
                    : undefined;
                return {
                    ...product,
                    formGroup,
                };
            });
        this.dataSource = new MatTableDataSource(data);
    }

    private subscribeToProductList$() {
        this.openDialog();
        this.productListService.productList$.pipe(takeUntil(this.ngUnsubscribe), filter(Boolean)).subscribe({
            next: (productList: ProductList) => {
                this.productList = productList;
                this.sampleOfferId = productList.sampleOfferId;
                this.createDataSourceFromProductList();
                this.closeDialog();
            },
            error: () => {
                this.onError('CONFIGURATION.SNACK_BAR.FETCH_CONFIGURATION.ERROR');
                this.closeDialog();
            },
        });
    }

    private onError(translationKey: string) {
        this.isLoading = false;
        this.snackBarService.openSnackBar({
            message: this.translateService.instant(translationKey),
            isFailure: true,
        });
    }

    private onSuccess(translationKey: string) {
        this.isLoading = false;
        this.snackBarService.openSnackBar({
            message: this.translateService.instant(translationKey),
        });
    }

    public ngOnDestroy(): void {
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }

    private openDialog(): void {
        if (!this.dialogRef) {
            const dialogConfig = new MatDialogConfig();
            dialogConfig.disableClose = true;
            dialogConfig.width = '250px';
            dialogConfig.scrollStrategy = new NoopScrollStrategy();
            this.dialogRef = this.dialog.open(LoadingOverlayComponent, dialogConfig);
        }
    }

    private closeDialog(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
            this.dialogRef = null;
        }
    }
}
